import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";

import {
  selectedReport,
  setBillingDiagnosisCodes,
  setShowEditDiagnosisModal,
} from "app/store/transmission/slice/transmissionSlice";
import { useUpdateDiagnosisCodesMutation } from "app/store/transmission/api/transmissionNotesApi";

import BillingDiagnosisCodeForm from "./BillingDiagnosisCodeForm";
import ModalWrapper from "components/common/modals/modalWrapper/ModalWrapper";

import { isValidArray } from "utils/array";
import { showErrorToast, showSuccessToast } from "utils/toast";
import { ADDITIONAL_BILLING_CODES } from "static/constants";
import { checkIsIDTFBilling } from "utils/billingCode.util";

const BillingDiagnosisCodeModal = () => {
  const {
    reportId,
    reportDetail,
    isLegacyReport,
    billingDiagnosisCodes,
    showEditDiagnosisModal,
    reportAccountDetail,
  } = useSelector((state) => state.transmission);

  const isIDTFBilling = checkIsIDTFBilling({
    account: reportAccountDetail,
    patient: reportDetail?.patient,
  });

  const dispatch = useDispatch();

  const [updateDiagnosisCodes, { isLoading }] =
    useUpdateDiagnosisCodesMutation();

  useEffect(() => {
    const codes = isValidArray(reportDetail?.billingDiagnosisCodes)
      ? reportDetail?.billingDiagnosisCodes
      : [
          {
            billingCode: reportDetail?.billing_code || "",
            diagnosisCode: reportDetail?.diagnosis_code || "",
          },
        ];

    dispatch(setBillingDiagnosisCodes(codes));

    return () => {
      dispatch(setBillingDiagnosisCodes([]));
    };
  }, [
    dispatch,
    reportDetail?.billingDiagnosisCodes,
    reportDetail?.billing_code,
    reportDetail?.diagnosis_code,
  ]);

  const handleCodeChange = (index, diagnosisCode) => {
    const updatedCodes = JSON.parse(JSON.stringify(billingDiagnosisCodes));
    updatedCodes[index].diagnosisCode = diagnosisCode;

    const { billingCode } = updatedCodes[index];

    const additionalCodes = ADDITIONAL_BILLING_CODES[billingCode];

    if (!isIDTFBilling && additionalCodes) {
      additionalCodes?.forEach((code) => {
        const codeIndex = updatedCodes.findIndex(
          (item) => item.billingCode === code
        );

        updatedCodes[codeIndex] = { billingCode: code, diagnosisCode };
      });
    }

    dispatch(setBillingDiagnosisCodes(updatedCodes));
  };

  const onClose = () => {
    dispatch(setShowEditDiagnosisModal(false));
  };

  const handleSubmit = async () => {
    try {
      const result = await updateDiagnosisCodes({
        reportId,
        billingDiagnosisCodes,
      }).unwrap();

      if (result?.data) {
        showSuccessToast(result?.message);

        dispatch(
          selectedReport({
            id: reportId,
            isLegacyReport: isLegacyReport,
            reportDetail: result?.data,
          })
        );

        dispatch(setShowEditDiagnosisModal(false));
      }
    } catch (err) {
      showErrorToast(err?.message);
    }
  };

  return (
    <ModalWrapper
      show={showEditDiagnosisModal}
      title="Update Diagnosis Codes"
      onClose={onClose}
      actionButtons={[
        {
          label: "Cancel",
          variant: "outlined",
          color: "secondary",
          onClick: onClose,
          disabled: isLoading,
        },
        {
          label: "Update",
          onClick: handleSubmit,
          isLoading: isLoading,
          disabled:
            isLoading ||
            billingDiagnosisCodes?.some(
              (code) => !code.billingCode?.trim() || !code.diagnosisCode?.trim()
            ) ||
            reportDetail?.billingDiagnosisCodes?.every((oldCode) =>
              billingDiagnosisCodes?.some(
                (newCode) => oldCode.diagnosisCode === newCode.diagnosisCode
              )
            ),
        },
      ]}
    >
      <BillingDiagnosisCodeForm
        isLoading={isLoading}
        handleCodeChange={handleCodeChange}
      />
    </ModalWrapper>
  );
};

export default BillingDiagnosisCodeModal;
